import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://demo.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'demo-key'

// Para desenvolvimento, criar um cliente mock se as variáveis não estiverem definidas
const isDevelopment = !import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY

if (isDevelopment) {
  console.warn('⚠️ Usando cliente Supabase mock para desenvolvimento. Configure as variáveis de ambiente para produção.')
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'bizuadmin-auth',
    // Garantir persistência via localStorage
    storage: typeof window !== 'undefined' ? window.localStorage : undefined
  }
})

// Tipos para o banco de dados
export interface Database {
  public: {
    Tables: {
      profiles: {
        Row: {
          id: string
          full_name: string | null
          avatar_url: string | null
          role: 'admin' | 'professor' | 'aluno'
          created_at: string
          updated_at: string
        }
        Insert: {
          id: string
          full_name?: string | null
          avatar_url?: string | null
          role?: 'admin' | 'professor' | 'aluno'
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          full_name?: string | null
          avatar_url?: string | null
          role?: 'admin' | 'professor' | 'aluno'
          created_at?: string
          updated_at?: string
        }
      }
      aulas: {
        Row: {
          id: string
          title: string
          description: string | null
          video_url: string | null
          embed_url: string | null
          duration: string | null
          created_by: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description?: string | null
          video_url?: string | null
          embed_url?: string | null
          duration?: string | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string | null
          video_url?: string | null
          embed_url?: string | null
          duration?: string | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      testes: {
        Row: {
          id: string
          title: string
          description: string | null
          aula_id: string | null
          tempo_limite: number | null
          is_published: boolean | null
          created_by: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          description?: string | null
          aula_id?: string | null
          tempo_limite?: number | null
          is_published?: boolean | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          description?: string | null
          aula_id?: string | null
          tempo_limite?: number | null
          is_published?: boolean | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      questoes: {
        Row: {
          id: string
          enunciado: string
          teste_id: string | null
          ordem: number
          tri_a: number | null
          tri_b: number | null
          tri_c: number | null
          created_by: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          enunciado: string
          teste_id?: string | null
          ordem: number
          tri_a?: number | null
          tri_b?: number | null
          tri_c?: number | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          enunciado?: string
          teste_id?: string | null
          ordem?: number
          tri_a?: number | null
          tri_b?: number | null
          tri_c?: number | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
      }
      alternativas: {
        Row: {
          id: string
          questao_id: string
          texto: string
          correta: boolean | null
          created_at: string
        }
        Insert: {
          id?: string
          questao_id: string
          texto: string
          correta?: boolean | null
          created_at?: string
        }
        Update: {
          id?: string
          questao_id?: string
          texto?: string
          correta?: boolean | null
          created_at?: string
        }
      }
      resultados: {
        Row: {
          id: string
          aluno_id: string | null
          teste_id: string | null
          score: number
          percentual_acertos: number | null
          tri_theta: number | null
          tri_score_saeb: number | null
          tri_standard_error: number | null
          started_at: string | null
          completed_at: string | null
          created_at: string
        }
        Insert: {
          id?: string
          aluno_id?: string | null
          teste_id?: string | null
          score: number
          percentual_acertos?: number | null
          tri_theta?: number | null
          tri_score_saeb?: number | null
          tri_standard_error?: number | null
          started_at?: string | null
          completed_at?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          aluno_id?: string | null
          teste_id?: string | null
          score?: number
          percentual_acertos?: number | null
          tri_theta?: number | null
          tri_score_saeb?: number | null
          tri_standard_error?: number | null
          started_at?: string | null
          completed_at?: string | null
          created_at?: string
        }
      }
      respostas: {
        Row: {
          id: string
          resultado_id: string | null
          questao_id: string | null
          resposta_selecionada: string | null
          correta: boolean | null
          tempo_resposta: number | null
          created_at: string
        }
        Insert: {
          id?: string
          resultado_id?: string | null
          questao_id?: string | null
          resposta_selecionada?: string | null
          correta?: boolean | null
          tempo_resposta?: number | null
          created_at?: string
        }
        Update: {
          id?: string
          resultado_id?: string | null
          questao_id?: string | null
          resposta_selecionada?: string | null
          correta?: boolean | null
          tempo_resposta?: number | null
          created_at?: string
        }
      }
      noticias: {
        Row: {
          id: string
          title: string
          content: string | null
          excerpt: string | null
          image: string | null
          featured: boolean | null
          created_by: string | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          title: string
          content?: string | null
          excerpt?: string | null
          image?: string | null
          featured?: boolean | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          title?: string
          content?: string | null
          excerpt?: string | null
          image?: string | null
          featured?: boolean | null
          created_by?: string | null
          created_at?: string
          updated_at?: string
        }
      },
      turmas: {
        Row: {
          id: string
          nome: string
          descricao: string | null
          created_by: string | null
          created_at: string
        }
        Insert: {
          id?: string
          nome: string
          descricao?: string | null
          created_by?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          nome?: string
          descricao?: string | null
          created_by?: string | null
          created_at?: string
        }
      },
      turma_alunos: {
        Row: {
          id: string
          turma_id: string
          aluno_id: string | null
          email: string | null
          created_at: string
        }
        Insert: {
          id?: string
          turma_id: string
          aluno_id?: string | null
          email?: string | null
          created_at?: string
        }
        Update: {
          id?: string
          turma_id?: string
          aluno_id?: string | null
          email?: string | null
          created_at?: string
        }
      },
      turma_testes: {
        Row: {
          id: string
          turma_id: string
          teste_id: string
          created_at: string
        }
        Insert: {
          id?: string
          turma_id: string
          teste_id: string
          created_at?: string
        }
        Update: {
          id?: string
          turma_id?: string
          teste_id?: string
          created_at?: string
        }
      },
      turma_aulas: {
        Row: {
          id: string
          turma_id: string
          aula_id: string
          created_at: string
        }
        Insert: {
          id?: string
          turma_id: string
          aula_id: string
          created_at?: string
        }
        Update: {
          id?: string
          turma_id?: string
          aula_id?: string
          created_at?: string
        }
      },
      tentativas_testes: {
        Row: {
          id: string
          user_id: string
          teste_id: string
          started_at: string | null
          completed_at: string | null
          score: number | null
          nota: number | null
          is_archived: boolean | null
          created_at: string
          updated_at: string
        }
        Insert: {
          id?: string
          user_id: string
          teste_id: string
          started_at?: string | null
          completed_at?: string | null
          score?: number | null
          nota?: number | null
          is_archived?: boolean | null
          created_at?: string
          updated_at?: string
        }
        Update: {
          id?: string
          user_id?: string
          teste_id?: string
          started_at?: string | null
          completed_at?: string | null
          score?: number | null
          nota?: number | null
          is_archived?: boolean | null
          created_at?: string
          updated_at?: string
        }
      }
    }
  }
}